import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getPatientPoints,
  grantPoints,
  listPatientPointsEntries,
  redeemPoints,
  reversePointsEntry,
  type GrantPointsPayload,
  type RedeemPointsPayload,
} from '../../../api/points';
import { useAuth } from '../../auth/AuthProvider';

function useRequireToken() {
  const { tokens } = useAuth();
  return () => {
    if (!tokens?.access_token) {
      throw new Error('Authenticated admin session is required.');
    }
    return tokens.access_token;
  };
}

export function patientPointsKey(patientId: string) {
  return ['patient-points', patientId] as const;
}

export function patientPointsEntriesKey(patientId: string, page: number, pageSize: number) {
  return ['patient-points-entries', patientId, page, pageSize] as const;
}

export function usePatientPoints(patientId: string) {
  const getToken = useRequireToken();

  return useQuery({
    queryKey: patientPointsKey(patientId),
    queryFn: () => getPatientPoints(patientId, getToken()),
    enabled: Boolean(patientId),
  });
}

export function usePatientPointsEntries(patientId: string, page: number, pageSize: number) {
  const getToken = useRequireToken();

  return useQuery({
    queryKey: patientPointsEntriesKey(patientId, page, pageSize),
    queryFn: () => listPatientPointsEntries(patientId, { page, pageSize }, getToken()),
    enabled: Boolean(patientId),
  });
}

/**
 * Every write invalidates both the summary and every page of the ledger — a
 * grant changes the balance and inserts a row, and a reversal changes the row
 * it points at as well as the new one.
 */
function useInvalidatePoints(patientId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: patientPointsKey(patientId) }),
      queryClient.invalidateQueries({ queryKey: ['patient-points-entries', patientId] }),
    ]);
  };
}

export function useGrantPoints(patientId: string) {
  const getToken = useRequireToken();
  const invalidate = useInvalidatePoints(patientId);

  return useMutation({
    mutationFn: (payload: GrantPointsPayload) => grantPoints(patientId, payload, getToken()),
    onSuccess: invalidate,
  });
}

export function useRedeemPoints(patientId: string) {
  const getToken = useRequireToken();
  const invalidate = useInvalidatePoints(patientId);

  return useMutation({
    mutationFn: (payload: RedeemPointsPayload) => redeemPoints(patientId, payload, getToken()),
    onSuccess: invalidate,
  });
}

export function useReversePointsEntry(patientId: string) {
  const getToken = useRequireToken();
  const invalidate = useInvalidatePoints(patientId);

  return useMutation({
    mutationFn: ({ entryId, note }: { readonly entryId: string; readonly note?: string | null }) =>
      reversePointsEntry(patientId, entryId, { note }, getToken()),
    onSuccess: invalidate,
  });
}
